import React, { useMemo } from 'react';
import { Gauge } from './DashboardCockpit';
import { Wallet, Calendar, TrendingUp, Timer, GaugeCircle } from 'lucide-react';

/**
 * Tenant portal cockpit — 4 animated gauges + status header.
 * Props:
 *   profile: response of /api/tenant/profile
 */
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const monthsBetween = (start) => {
  if (!start) return 0;
  try {
    const s = new Date(start);
    const n = new Date();
    return (n.getFullYear() - s.getFullYear()) * 12 + (n.getMonth() - s.getMonth());
  } catch { return 0; }
};

const daysUntilNextRent = () => {
  // Rent due on the 5th of each month
  const n = new Date();
  let due = new Date(n.getFullYear(), n.getMonth(), 5);
  if (due <= n) due = new Date(n.getFullYear(), n.getMonth() + 1, 5);
  return Math.max(0, Math.ceil((due - n) / (1000 * 60 * 60 * 24)));
};

const TenantCockpit = ({ profile }) => {
  const { totalDue, unpaidAmount, unpaidPct, monthsActive, paymentsCount, onTimePct, daysToNext } = useMemo(() => {
    const stats = profile?.stats || {};
    const lease = profile?.lease || {};
    const totalDue = stats.total_due || 0;
    const unpaid = stats.unpaid_amount || 0;
    const unpaidPct = totalDue > 0 ? Math.min(100, Math.round((unpaid / totalDue) * 100)) : 0;
    const monthsActive = stats.months_active || monthsBetween(lease.start_date);
    const paymentsCount = stats.payments_count || 0;
    // On-time payment rate = payments_count / months_active
    const onTimePct = monthsActive > 0 ? Math.min(100, Math.round((paymentsCount / monthsActive) * 100)) : 0;
    return {
      totalDue,
      unpaidAmount: unpaid,
      unpaidPct,
      monthsActive,
      paymentsCount,
      onTimePct,
      daysToNext: daysUntilNextRent(),
    };
  }, [profile]);

  if (!profile?.lease?.start_date) return null;

  return (
    <div
      className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3] p-4 md:p-6 mb-6 shadow-lg"
      data-testid="tenant-cockpit"
    >
      <div className="flex items-center gap-2 mb-4">
        <GaugeCircle className="w-5 h-5 text-amber-700" />
        <h2 className="text-sm md:text-base font-bold text-[#1B3A4B] uppercase tracking-wider">
          Cockpit locataire
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Unpaid balance */}
        <div className="flex flex-col items-center" data-testid="gauge-unpaid">
          <Gauge
            value={unpaidPct}
            max={100}
            size={170}
            thickness={13}
            label="% Impayé"
            sub={`${fmt(unpaidAmount)} FCFA`}
            color="#EF4444"
            warningBelow={30}
            dangerBelow={0}
            startupDelay={100}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Sur {fmt(totalDue)} FCFA dû
          </div>
        </div>
        {/* 2. Lease tenure */}
        <div className="flex flex-col items-center" data-testid="gauge-tenure">
          <Gauge
            value={Math.min(monthsActive, 60)}
            max={60}
            size={170}
            thickness={13}
            label={`${monthsActive} mois`}
            sub="d'occupation"
            color="#10B981"
            startupDelay={250}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Depuis le {new Date(profile.lease.start_date).toLocaleDateString('fr-FR')}
          </div>
        </div>
        {/* 3. On-time payment ratio */}
        <div className="flex flex-col items-center" data-testid="gauge-ontime">
          <Gauge
            value={onTimePct}
            max={100}
            size={170}
            thickness={13}
            label={`${onTimePct}%`}
            sub={`${paymentsCount}/${monthsActive} mois payés`}
            color="#3B82F6"
            warningBelow={70}
            dangerBelow={40}
            startupDelay={400}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Ponctualité
          </div>
        </div>
        {/* 4. Days until next payment */}
        <div className="flex flex-col items-center" data-testid="gauge-nextpay">
          <Gauge
            value={30 - daysToNext}
            max={30}
            size={170}
            thickness={13}
            label={`${daysToNext} j`}
            sub="avant échéance"
            color={daysToNext <= 5 ? '#EF4444' : daysToNext <= 10 ? '#F59E0B' : '#D4A745'}
            startupDelay={550}
          />
          <div className="text-[10px] uppercase font-semibold text-gray-500 mt-1 flex items-center gap-1">
            <Timer className="w-3 h-3" /> Prochain loyer (5 du mois)
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantCockpit;
